import React, { useCallback, useEffect, useMemo, useState } from 'react'
import FilterPanelCheckboxGroup, { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import { convertPriceToPoints } from 'constants/merchandise'
import { MerchandiseColourOption } from '../MerchandiseColourOption'
import { useIntl } from 'react-intl'

const PointsConversionText = styled(Caption)`
  margin-top: ${rem(4)};
`

interface Props {
  facet: App.MerchandiseAnySearchFacet
  defaultSelected?: Array<string> | { min?: number, max?: number }
  onChange?: () => void
  onRangeCommit?: (filter: string, start?: number, end?: number) => void
}

function MerchandiseSearchFilter(props: Props) {
  const { facet, defaultSelected, onChange, onRangeCommit } = props
  const intl = useIntl()

  const { type, filter } = facet

  // Store user's current slider selections in state for points display and commit callbacks
  const initialMin = !Array.isArray(defaultSelected) ? defaultSelected?.min : undefined
  const initialMax = !Array.isArray(defaultSelected) ? defaultSelected?.max : undefined
  const [userMin, setUserMin] = useState<number | undefined>(initialMin)
  const [userMax, setUserMax] = useState<number | undefined>(initialMax)

  // Reset state when filters are cleared externally (e.g., "Reset all" clicked)
  useEffect(() => {
    if (!Array.isArray(defaultSelected)) {
      const urlMin = defaultSelected?.min
      const urlMax = defaultSelected?.max

      // Clear state when URL has no range values
      if (urlMin === undefined && urlMax === undefined && (userMin !== undefined || userMax !== undefined)) {
        setUserMin(undefined)
        setUserMax(undefined)
      }
    }
  }, [defaultSelected, userMin, userMax])

  const formatPointsValue = useCallback((value: number) => {
    return value.toLocaleString('en-AU')
  }, [])

  const checkItems = useMemo((): Array<FilterPanelCheckItem> => {
    if (facet.type === 'count') {
      const isColour = facet.displayType === 'colour'

      return facet.values.map((value): FilterPanelCheckItem => {
        const isSelected = Array.isArray(defaultSelected) && defaultSelected.includes(value.value)

        const label = isColour ? (
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            <MerchandiseColourOption
              colors={[value.hex]}
              size={18}
              selected={isSelected}
              interactive={false}
            />
            {value.label}
          </Group>
        ) : value.label

        return {
          label,
          value: value.value,
          count: value.count,
          hideCheckbox: isColour,
        }
      })
    }

    return []
  }, [facet, defaultSelected])

  const onStartChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUserMin(event.currentTarget.valueAsNumber)
  }, [])

  const onEndChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUserMax(event.currentTarget.valueAsNumber)
  }, [])

  // Helper to check if value is at boundary (within 1, not including 1)
  const isAtBoundary = useCallback((value: number, boundary: number) => {
    return Math.abs(value - boundary) < 1
  }, [])

  const commitRange = useCallback((startValue: number, endValue: number) => {
    if (facet.type !== 'range') return

    // Don't send parameter if at boundary
    const startCommit = isAtBoundary(startValue, facet.min) ? undefined : startValue
    const endCommit = isAtBoundary(endValue, facet.max) ? undefined : endValue

    onRangeCommit?.(String(filter), startCommit, endCommit)
  }, [facet, filter, onRangeCommit, isAtBoundary])

  const onStartCommit = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (facet.type !== 'range') return

    const startValue = event.currentTarget.valueAsNumber
    const endValue = userMax ?? initialMax ?? facet.max

    commitRange(startValue, endValue)
  }, [facet, userMax, initialMax, commitRange])

  const onEndCommit = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (facet.type !== 'range') return

    const startValue = userMin ?? initialMin ?? facet.min
    const endValue = event.currentTarget.valueAsNumber

    commitRange(startValue, endValue)
  }, [facet, userMin, initialMin, commitRange])

  // Create selected set for controlled mode (handles URL state on page load)
  const selectedSet = useMemo(() => {
    if (Array.isArray(defaultSelected)) {
      return new Set(defaultSelected)
    }
    return undefined
  }, [defaultSelected])

  switch (type) {
    case 'count':
      return <FilterPanelCheckboxGroup
        name={filter}
        items={checkItems}
        maxItems={5}
        selected={selectedSet}
        onChange={onChange}
      />
    case 'range': {
      // Range slider and points conversion; commit range directly via onRangeCommit
      if (facet.type !== 'range') return null

      const minPrice = userMin ?? initialMin ?? facet.min
      const maxPrice = userMax ?? initialMax ?? facet.max
      const minPoints = convertPriceToPoints(minPrice)
      const maxPoints = convertPriceToPoints(maxPrice)

      return <>
        <DualSliderInput
          key={`${filter}-${facet.min}-${facet.max}`}
          variant="label"
          format={filter === 'price' ? 'currency' : filter === 'points' ? 'custom' : 'none'}
          formatValue={filter === 'points' ? formatPointsValue : undefined}
          min={facet.min}
          max={facet.max}
          defaultStartValue={!Array.isArray(defaultSelected) ? defaultSelected?.min : undefined}
          defaultEndValue={!Array.isArray(defaultSelected) ? defaultSelected?.max : undefined}
          onStartChange={onStartChange}
          onEndChange={onEndChange}
          onStartCommit={onStartCommit}
          onEndCommit={onEndCommit}
        />
        {/* Show points conversion for price filter */}
        {filter === 'price' && (
          <Group direction="horizontal" gap={8} horizontalAlign="space-between">
            <PointsConversionText variant="small" colour="neutral-three">
              {intl.formatMessage(
                {
                  id: 'r9SfzH7cJH',
                  defaultMessage: '{formattedPoints} pts',
                  description: 'Points value display in merchandise price filter',
                },
                { formattedPoints: formatPointsValue(minPoints) },
              )}
            </PointsConversionText>
            <PointsConversionText variant="small" colour="neutral-three">
              {intl.formatMessage(
                {
                  id: 'r9SfzH7cJH',
                  defaultMessage: '{formattedPoints} pts',
                  description: 'Points value display in merchandise price filter',
                },
                { formattedPoints: formatPointsValue(maxPoints) },
              )}
            </PointsConversionText>
          </Group>
        )}
      </>
    }
    default:
      return null
  }
}

export default MerchandiseSearchFilter
